"use client";

import type { Frontmatter } from "@/lib/frontmatter/types";
import { FieldRow } from "./field-section";
import { TextField } from "./text-field";
import { ListField } from "./list-field";
import { EnumField } from "./enum-field";
import { DateField } from "./date-field";
import { SingleUserField, MultiUserField } from "./user-chip";
import { DocLinkChips, SingleDocLink } from "./doc-link-chips";
import { TagBadges } from "./tag-badges";
import { Switch } from "@workspace/ui/components/switch";

interface TypeSpecificSectionProps {
  frontmatter: Frontmatter;
  editing: boolean;
  onUpdate: (key: string, value: unknown) => void;
}

export function TypeSpecificSection({
  frontmatter,
  editing,
  onUpdate,
}: TypeSpecificSectionProps) {
  const fm = frontmatter as unknown as Record<string, unknown>;

  switch (frontmatter.type) {
    case "adr":
      return (
        <>
          <FieldRow label="Decision date">
            <DateField
              value={fm.decision_date as string}
              editing={editing}
              onChange={(v) => onUpdate("decision_date", v)}
            />
          </FieldRow>
          <FieldRow label="Deciders">
            <MultiUserField
              value={fm.decision_makers as string[]}
              editing={editing}
              onChange={(v) => onUpdate("decision_makers", v)}
            />
          </FieldRow>
          <FieldRow label="Problem">
            <TextField
              value={fm.problem_statement as string}
              editing={editing}
              onChange={(v) => onUpdate("problem_statement", v)}
              multiline
            />
          </FieldRow>
          <FieldRow label="Decision">
            <TextField
              value={fm.decision_summary as string}
              editing={editing}
              onChange={(v) => onUpdate("decision_summary", v)}
              multiline
            />
          </FieldRow>
          <FieldRow label="Trade-offs">
            <ListField
              value={fm.trade_offs as string[]}
              editing={editing}
              onChange={(v) => onUpdate("trade_offs", v)}
            />
          </FieldRow>
          <FieldRow label="Assumptions">
            <ListField
              value={fm.assumptions as string[]}
              editing={editing}
              onChange={(v) => onUpdate("assumptions", v)}
            />
          </FieldRow>
          <FieldRow label="Invalidation">
            <ListField
              value={fm.invalidation_triggers as string[]}
              editing={editing}
              onChange={(v) => onUpdate("invalidation_triggers", v)}
              placeholder="Add trigger + Enter"
            />
          </FieldRow>
        </>
      );

    case "prd":
      return (
        <>
          <FieldRow label="Product">
            <TextField value={fm.product as string} editing={editing} onChange={(v) => onUpdate("product", v)} />
          </FieldRow>
          <FieldRow label="Feature">
            <TextField value={fm.feature as string} editing={editing} onChange={(v) => onUpdate("feature", v)} />
          </FieldRow>
          <FieldRow label="Priority">
            <EnumField value={fm.priority as string} options={["p0", "p1", "p2", "p3"]} editing={editing} onChange={(v) => onUpdate("priority", v)} />
          </FieldRow>
          <FieldRow label="Target">
            <TextField value={fm.target_release as string} editing={editing} onChange={(v) => onUpdate("target_release", v)} />
          </FieldRow>
          <FieldRow label="Stakeholders">
            <MultiUserField value={fm.stakeholders as string[]} editing={editing} onChange={(v) => onUpdate("stakeholders", v)} />
          </FieldRow>
          <FieldRow label="Success">
            <ListField value={fm.success_metrics as string[]} editing={editing} onChange={(v) => onUpdate("success_metrics", v)} />
          </FieldRow>
          <FieldRow label="Out of scope">
            <ListField value={fm.out_of_scope as string[]} editing={editing} onChange={(v) => onUpdate("out_of_scope", v)} />
          </FieldRow>
        </>
      );

    case "rfc":
      return (
        <>
          <FieldRow label="RFC #">
            <TextField value={String(fm.rfc_number ?? "")} editing={editing} onChange={(v) => onUpdate("rfc_number", parseInt(v) || null)} />
          </FieldRow>
          <FieldRow label="Deadline">
            <DateField value={fm.discussion_deadline as string} editing={editing} onChange={(v) => onUpdate("discussion_deadline", v)} />
          </FieldRow>
          <FieldRow label="Vote req.">
            {editing ? (
              <Switch checked={!!fm.voting_required} onCheckedChange={(v) => onUpdate("voting_required", v)} />
            ) : (
              <span className="text-sm">{fm.voting_required ? "Yes" : "No"}</span>
            )}
          </FieldRow>
          <FieldRow label="Questions">
            <ListField value={fm.open_questions as string[]} editing={editing} onChange={(v) => onUpdate("open_questions", v)} placeholder="Add question + Enter" />
          </FieldRow>
        </>
      );

    case "tdd":
      return (
        <>
          <FieldRow label="Services">
            <ListField value={fm.services_affected as string[]} editing={editing} onChange={(v) => onUpdate("services_affected", v)} />
          </FieldRow>
          <FieldRow label="Tech stack">
            <TagBadges value={fm.tech_stack as string[]} editing={editing} onChange={(v) => onUpdate("tech_stack", v)} />
          </FieldRow>
          <FieldRow label="Complexity">
            <EnumField value={fm.estimated_complexity as string} options={["xs", "s", "m", "l", "xl"]} editing={editing} onChange={(v) => onUpdate("estimated_complexity", v)} />
          </FieldRow>
          <FieldRow label="Open decisions">
            <ListField value={fm.open_decisions as string[]} editing={editing} onChange={(v) => onUpdate("open_decisions", v)} />
          </FieldRow>
        </>
      );

    case "runbook":
      return (
        <>
          <FieldRow label="Service">
            <TextField value={fm.service as string} editing={editing} onChange={(v) => onUpdate("service", v)} />
          </FieldRow>
          <FieldRow label="Type">
            <EnumField value={fm.runbook_type as string} options={["deployment", "rollback", "scaling", "incident", "maintenance", "debug"]} editing={editing} onChange={(v) => onUpdate("runbook_type", v)} />
          </FieldRow>
          <FieldRow label="Severity">
            <EnumField value={fm.severity_applicability as string} options={["sev1", "sev2", "sev3", "routine"]} editing={editing} onChange={(v) => onUpdate("severity_applicability", v)} />
          </FieldRow>
          <FieldRow label="Duration">
            <TextField value={fm.estimated_duration as string} editing={editing} onChange={(v) => onUpdate("estimated_duration", v)} />
          </FieldRow>
          <FieldRow label="Prerequisites">
            <ListField value={fm.prerequisites as string[]} editing={editing} onChange={(v) => onUpdate("prerequisites", v)} />
          </FieldRow>
          <FieldRow label="On-call">
            <SingleUserField value={fm.oncall_contact as string} editing={editing} onChange={(v) => onUpdate("oncall_contact", v)} />
          </FieldRow>
          <FieldRow label="Last tested">
            <DateField value={fm.last_tested as string} editing={editing} onChange={(v) => onUpdate("last_tested", v)} />
          </FieldRow>
        </>
      );

    case "postmortem":
      return (
        <>
          <FieldRow label="Incident">
            <TextField value={fm.incident_id as string} editing={editing} onChange={(v) => onUpdate("incident_id", v)} />
          </FieldRow>
          <FieldRow label="Severity">
            <EnumField value={fm.severity as string} options={["sev1", "sev2", "sev3"]} editing={editing} onChange={(v) => onUpdate("severity", v)} />
          </FieldRow>
          <FieldRow label="Date">
            <DateField value={fm.incident_date as string} editing={editing} onChange={(v) => onUpdate("incident_date", v)} />
          </FieldRow>
          <FieldRow label="Duration">
            <TextField value={fm.duration as string} editing={editing} onChange={(v) => onUpdate("duration", v)} />
          </FieldRow>
          <FieldRow label="Services">
            <ListField value={fm.services_affected as string[]} editing={editing} onChange={(v) => onUpdate("services_affected", v)} />
          </FieldRow>
          <FieldRow label="Factors">
            <ListField value={fm.contributing_factors as string[]} editing={editing} onChange={(v) => onUpdate("contributing_factors", v)} />
          </FieldRow>
          <FieldRow label="Follow-up">
            <DocLinkChips value={fm.follow_up_adrs as string[]} editing={editing} onChange={(v) => onUpdate("follow_up_adrs", v)} />
          </FieldRow>
        </>
      );

    case "slo":
      return (
        <>
          <FieldRow label="Service">
            <TextField value={fm.service as string} editing={editing} onChange={(v) => onUpdate("service", v)} />
          </FieldRow>
          <FieldRow label="SLO type">
            <EnumField value={fm.slo_type as string} options={["availability", "latency", "error_rate", "throughput", "custom"]} editing={editing} onChange={(v) => onUpdate("slo_type", v)} />
          </FieldRow>
          <FieldRow label="Target">
            <TextField value={fm.target as string} editing={editing} onChange={(v) => onUpdate("target", v)} />
          </FieldRow>
          <FieldRow label="Window">
            <TextField value={fm.measurement_window as string} editing={editing} onChange={(v) => onUpdate("measurement_window", v)} />
          </FieldRow>
          <FieldRow label="Error budget">
            <TextField value={fm.error_budget as string} editing={editing} onChange={(v) => onUpdate("error_budget", v)} />
          </FieldRow>
        </>
      );

    case "service-catalog":
      return (
        <>
          <FieldRow label="Service">
            <TextField value={fm.service_name as string} editing={editing} onChange={(v) => onUpdate("service_name", v)} />
          </FieldRow>
          <FieldRow label="Type">
            <EnumField value={fm.service_type as string} options={["api", "worker", "frontend", "database", "infra", "library", "cli"]} editing={editing} onChange={(v) => onUpdate("service_type", v)} />
          </FieldRow>
          <FieldRow label="Tier">
            <EnumField value={fm.tier as string} options={["critical", "important", "standard", "experimental"]} editing={editing} onChange={(v) => onUpdate("tier", v)} />
          </FieldRow>
          <FieldRow label="On-call">
            <SingleUserField value={fm.oncall as string} editing={editing} onChange={(v) => onUpdate("oncall", v)} />
          </FieldRow>
          <FieldRow label="Tech stack">
            <TagBadges value={fm.tech_stack as string[]} editing={editing} onChange={(v) => onUpdate("tech_stack", v)} />
          </FieldRow>
        </>
      );

    case "deprecation":
      return (
        <>
          <FieldRow label="Target">
            <TextField value={fm.target as string} editing={editing} onChange={(v) => onUpdate("target", v)} />
          </FieldRow>
          <FieldRow label="Type">
            <EnumField value={fm.target_type as string} options={["service", "api", "endpoint", "library", "feature", "config"]} editing={editing} onChange={(v) => onUpdate("target_type", v)} />
          </FieldRow>
          <FieldRow label="Deprecated">
            <DateField value={fm.deprecation_date as string} editing={editing} onChange={(v) => onUpdate("deprecation_date", v)} />
          </FieldRow>
          <FieldRow label="EOL">
            <DateField value={fm.eol_date as string} editing={editing} onChange={(v) => onUpdate("eol_date", v)} />
          </FieldRow>
          <FieldRow label="Migration">
            <TextField value={fm.migration_path as string} editing={editing} onChange={(v) => onUpdate("migration_path", v)} multiline />
          </FieldRow>
          <FieldRow label="Consumers">
            <ListField value={fm.affected_consumers as string[]} editing={editing} onChange={(v) => onUpdate("affected_consumers", v)} />
          </FieldRow>
          <FieldRow label="Breaking">
            <ListField value={fm.breaking_changes as string[]} editing={editing} onChange={(v) => onUpdate("breaking_changes", v)} />
          </FieldRow>
        </>
      );

    case "tech-debt":
      return (
        <>
          <FieldRow label="Debt type">
            <EnumField value={fm.debt_type as string} options={["architectural", "code-quality", "test-coverage", "documentation", "security", "performance", "dependency", "operational"]} editing={editing} onChange={(v) => onUpdate("debt_type", v)} />
          </FieldRow>
          <FieldRow label="Severity">
            <EnumField value={fm.severity as string} options={["critical", "high", "medium", "low"]} editing={editing} onChange={(v) => onUpdate("severity", v)} />
          </FieldRow>
          <FieldRow label="Effort">
            <EnumField value={fm.estimated_effort as string} options={["hours", "days", "weeks", "months", "quarters"]} editing={editing} onChange={(v) => onUpdate("estimated_effort", v)} />
          </FieldRow>
          <FieldRow label="Incurred">
            <DateField value={fm.incurred_date as string} editing={editing} onChange={(v) => onUpdate("incurred_date", v)} />
          </FieldRow>
          <FieldRow label="Impact">
            <TextField value={fm.impact as string} editing={editing} onChange={(v) => onUpdate("impact", v)} multiline />
          </FieldRow>
          <FieldRow label="Accepted">
            {editing ? (
              <Switch checked={!!fm.accepted_risk} onCheckedChange={(v) => onUpdate("accepted_risk", v)} />
            ) : (
              <span className="text-sm">{fm.accepted_risk ? "Yes" : "No"}</span>
            )}
          </FieldRow>
        </>
      );

    case "api-contract":
      return (
        <>
          <FieldRow label="API name">
            <TextField value={fm.api_name as string} editing={editing} onChange={(v) => onUpdate("api_name", v)} />
          </FieldRow>
          <FieldRow label="Version">
            <TextField value={fm.version as string} editing={editing} onChange={(v) => onUpdate("version", v)} />
          </FieldRow>
          <FieldRow label="Protocol">
            <EnumField value={fm.protocol as string} options={["rest", "grpc", "graphql", "websocket", "event", "cli"]} editing={editing} onChange={(v) => onUpdate("protocol", v)} />
          </FieldRow>
          <FieldRow label="Stability">
            <EnumField value={fm.stability as string} options={["stable", "beta", "alpha", "experimental", "deprecated"]} editing={editing} onChange={(v) => onUpdate("stability", v)} />
          </FieldRow>
          <FieldRow label="Base URL">
            <TextField value={fm.base_url as string} editing={editing} onChange={(v) => onUpdate("base_url", v)} />
          </FieldRow>
        </>
      );

    case "feature-brief":
      return (
        <>
          <FieldRow label="Problem">
            <TextField value={fm.problem_statement as string} editing={editing} onChange={(v) => onUpdate("problem_statement", v)} multiline />
          </FieldRow>
          <FieldRow label="Hypothesis">
            <TextField value={fm.hypothesis as string} editing={editing} onChange={(v) => onUpdate("hypothesis", v)} multiline />
          </FieldRow>
          <FieldRow label="Size">
            <EnumField value={fm.estimated_size as string} options={["xs", "s", "m", "l", "xl"]} editing={editing} onChange={(v) => onUpdate("estimated_size", v)} />
          </FieldRow>
          <FieldRow label="Quarter">
            <TextField value={fm.target_quarter as string} editing={editing} onChange={(v) => onUpdate("target_quarter", v)} />
          </FieldRow>
          <FieldRow label="Success">
            <ListField value={fm.success_criteria as string[]} editing={editing} onChange={(v) => onUpdate("success_criteria", v)} />
          </FieldRow>
          <FieldRow label="To PRD">
            {editing ? (
              <Switch checked={!!fm.escalates_to_prd} onCheckedChange={(v) => onUpdate("escalates_to_prd", v)} />
            ) : (
              <span className="text-sm">{fm.escalates_to_prd ? "Yes" : "No"}</span>
            )}
          </FieldRow>
          {fm.related_prd && (
            <FieldRow label="PRD">
              <SingleDocLink value={fm.related_prd as string} editing={editing} onChange={(v) => onUpdate("related_prd", v)} />
            </FieldRow>
          )}
        </>
      );

    case "decision-log":
      return (
        <>
          <FieldRow label="Context">
            <TextField value={fm.context as string} editing={editing} onChange={(v) => onUpdate("context", v)} multiline />
          </FieldRow>
          <FieldRow label="Decision">
            <TextField value={fm.decision as string} editing={editing} onChange={(v) => onUpdate("decision", v)} multiline />
          </FieldRow>
          <FieldRow label="Rationale">
            <TextField value={fm.rationale as string} editing={editing} onChange={(v) => onUpdate("rationale", v)} multiline />
          </FieldRow>
          <FieldRow label="Alternatives">
            <ListField value={fm.alternatives as string[]} editing={editing} onChange={(v) => onUpdate("alternatives", v)} />
          </FieldRow>
          <FieldRow label="Escalate">
            {editing ? (
              <Switch checked={!!fm.escalate_to_adr} onCheckedChange={(v) => onUpdate("escalate_to_adr", v)} />
            ) : (
              <span className="text-sm">{fm.escalate_to_adr ? "Yes" : "No"}</span>
            )}
          </FieldRow>
        </>
      );

    // roadmap-snapshot, quarterly-review, onboarding have no type-specific fields
    default:
      return null;
  }
}
